<template>
  <div>
    <!--    Breadcrumbs-->
    <breadcrumb style="margin-left: -15px">
      <breadcrumb-item>
        <a href="/settings/company?tab=2">Company settings</a>
      </breadcrumb-item>
      <breadcrumb-item>
        <span>API requests</span>
      </breadcrumb-item>
    </breadcrumb>

    <loading-panel :loading="loading"></loading-panel>

    <card>
      <h4 class="card-title">API requests</h4>
      <info-hint variant="primary" class="mb-3">
        Requests made with this account's API keys are listed here for 90 days.
      </info-hint>

      <filter-bar
        :filters="filterSchema"
        :initial-value="filterValues"
        :result-count="totalRows"
        :per-page="perPage"
        :per-page-options="perPageOptions"
        :loading="refreshing"
        saved-filters-key="api-requests"
        @change="onFiltersChanged"
        @update:perPage="onPerPageChange"
      >
        <template #actions>
          <base-button v-if="can('download_data')" icon class="btn-link text-white" title="Download CSV" @click="downloadRequests">
            <i class="tim-icons icon-cloud-download-93"></i>
          </base-button>
        </template>
      </filter-bar>

      <data-table
        class="mt-3"
        :columns="columns"
        :rows="rows"
        row-key="id"
        :loading="refreshing"
        clickable
        :page="currentPage"
        :per-page="perPage"
        :total="totalRows"
        @update:page="onPageChange"
        @row-click="openRequest"
      >
        <template #cell(status)="{ row: r }">
          <span class="tag-chip text-white" :style="tagChipStyle(r.statusColor)">{{ r.status || 'No response' }}</span>
        </template>
        <template #cell(request)="{ row: r }">
          <span class="data-table__primary">{{ r.method }} {{ r.path }}</span>
          <div v-if="r.query" class="data-table__muted">{{ r.query }}</div>
        </template>
        <template #cell(ip)="{ row: r }">
          <span v-if="r.ip">{{ r.ip }}</span>
          <span v-else class="data-table__muted">Not recorded</span>
        </template>
        <template #cell(time)="{ row: r }">{{ r.timeText }}</template>
        <template #cell(duration)="{ row: r }">
          <span v-if="r.duration_ms !== null">{{ r.duration_ms }} ms</span>
          <span v-else class="data-table__muted">Not recorded</span>
        </template>
        <template #empty>
          <img src="/img/pandas/empty-custom-fields.png" alt="" class="empty-state-img empty-state-img--md" />
          <h4 class="mt-3 empty-state-text">{{ hasActiveFilters ? 'No requests match these filters' : 'No requests yet' }}</h4>
          <p class="data-table__muted">
            {{ hasActiveFilters ? 'Try a wider date range or fewer filters.' : 'Requests appear here as soon as your API key is used.' }}
          </p>
        </template>
      </data-table>
    </card>

    <api-request-drawer :show.sync="drawerOpen" :request="selected" :keys="keys" :bodies-loading="bodiesLoading" />

    <base-alert-dialog
      :show="dialog.show"
      :variant="dialog.variant"
      :title="dialog.title"
      :message="dialog.message"
      :show-cancel="dialog.showCancel"
      :confirm-text="dialog.confirmText"
      :confirm-variant="dialog.confirmVariant"
      @confirm="onDialogConfirm"
      @cancel="dialog.show = false"
    />
  </div>
</template>

<script setup lang="ts">
// Community API request log, server paged:
// GET /companies/{id}/api/requests?count&offset&from&to&status&method&path&ip&user_agent&request_id&key
// (companies-get-api-requests) returns { requests, total_rows, keys }. List rows carry
// has_request_body / has_response_body; the bodies themselves come from ?id=<row id>.
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
import axios from 'axios'
import moment from 'moment-timezone'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { pageOffset, syncPagingQuery, readPagingQuery, downloadCsv } from '@/util/listPage'
import { tagChipStyle } from '@/util/tagChip'
import { statusClass, STATUS_COLORS, queryString, rangeToUtc } from '@/util/apiRequestLog'
import type { DataTableColumn } from '@/components/DataTable.vue'
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb'
import BreadcrumbItem from '@/components/Breadcrumb/BreadcrumbItem'
import ApiRequestDrawer from './ApiRequestDrawer.vue'

type ApiKey = { id: number; api_id: string; status: number; creation_date: string }

const { proxy } = getCurrentInstance() as any
const companyId = localStorage.getItem('companyId')
const baseUrl = `${API_LOCATION}companies/${companyId}`

// The date filter is wall-clock in the group's timezone, the same zone the times are shown in.
const group = JSON.parse(localStorage.getItem('group') || '{}')
const tz: string = group.tz_name || moment.tz.guess()

const permissions: string[] = JSON.parse(localStorage.getItem('permissions') || '[]')
function can(scope: string) {
  return permissions.includes(scope)
}

const loading = ref(false)
// Starts true so the table shows its skeleton until the first fetch lands.
const refreshing = ref(true)
const requests = ref<any[]>([])
const keys = ref<ApiKey[]>([])
const totalRows = ref(0)

const drawerOpen = ref(false)
const selected = ref<any>(null)
const bodiesLoading = ref(false)

const perPageOptions = [10, 25, 50, 100]
const perPage = ref(25)
const currentPage = ref(1)
let didInitialFetch = false

const columns: DataTableColumn[] = [
  { key: 'status', label: 'Status', width: '110px' },
  { key: 'request', label: 'Request' },
  { key: 'ip', label: 'IP address' },
  { key: 'time', label: 'Time' },
  { key: 'duration', label: 'Duration', align: 'right' },
]

// --- Filters (server side) -------------------------------------------------------
const filterValues = ref<Record<string, any>>({
  date: { mode: 'days', days: '7', from: '', to: '' },
  status: [], method: [], path: '', ip: '', user_agent: '', request_id: '', key: '',
})

const filterSchema = computed(() => {
  const schema: any[] = [
    { key: 'date', type: 'daterange', label: 'Date', primary: true },
    {
      key: 'status', type: 'multiselect', label: 'Status', primary: true,
      options: [
        { label: 'Success', value: '2xx' },
        { label: 'Client error', value: '4xx' },
        { label: 'Server error', value: '5xx' },
        { label: 'No response', value: 'none' },
      ],
    },
    { key: 'method', type: 'multiselect', label: 'Method', primary: true, options: ['GET', 'POST', 'PUT', 'DELETE'].map(m => ({ label: m, value: m })) },
    { key: 'path', type: 'text', label: 'Endpoint', placeholder: '/bookings', operatorLabel: 'contains', primary: true },
    { key: 'ip', type: 'text', label: 'IP address', placeholder: 'Starts with', operatorLabel: 'starts with', primary: true },
    { key: 'user_agent', type: 'text', label: 'User agent', placeholder: 'Any part of the user agent', operatorLabel: 'contains', primary: true },
    { key: 'request_id', type: 'text', label: 'Request ID', placeholder: 'Exact request ID', operatorLabel: 'is', primary: true },
  ]
  // A key filter only helps once the account has had more than one key (a key was rolled).
  if (keys.value.length > 1) {
    schema.push({
      key: 'key', type: 'select', label: 'API key', primary: true,
      options: keys.value.map(k => ({ label: k.api_id + (Number(k.status) === 1 ? '' : ' (rolled)'), value: k.id })),
    })
  }
  return schema
})

// The date range alone does not count: it is always set.
const hasActiveFilters = computed(() => {
  const f = filterValues.value
  return (Array.isArray(f.status) && f.status.length > 0) || (Array.isArray(f.method) && f.method.length > 0)
    || !!f.path || !!f.ip || !!f.user_agent || !!f.request_id || !!f.key
})

function onFiltersChanged(values: Record<string, any>) {
  filterValues.value = { ...filterValues.value, ...values }
  if (!didInitialFetch) {
    didInitialFetch = true
    fetchRequests()
    return
  }
  currentPage.value = 1
  fetchRequests()
}

function onPerPageChange(value: number) {
  perPage.value = value
  currentPage.value = 1
  fetchRequests()
}

function onPageChange(page: number) {
  currentPage.value = page
  fetchRequests()
}

function filterParams(): URLSearchParams {
  const f = filterValues.value
  const params = new URLSearchParams()
  const range = rangeToUtc(f.date, tz)
  if (range.from) params.set('from', range.from)
  if (range.to) params.set('to', range.to)
  if (Array.isArray(f.status) && f.status.length) params.set('status', f.status.join(','))
  if (Array.isArray(f.method) && f.method.length) params.set('method', f.method.join(','))
  if (f.path) params.set('path', String(f.path).trim())
  if (f.ip) params.set('ip', String(f.ip).trim())
  if (f.user_agent) params.set('user_agent', String(f.user_agent).trim())
  if (f.request_id) params.set('request_id', String(f.request_id).trim())
  if (f.key) params.set('key', String(f.key))
  return params
}

function listQuery(count: number, offset: number): string {
  const params = filterParams()
  params.set('count', String(count))
  params.set('offset', String(offset))
  return `${baseUrl}/api/requests?${params.toString()}`
}

// --- Row shaping ---------------------------------------------------------------
const rows = computed(() => requests.value.map(r => ({
  ...r,
  statusColor: STATUS_COLORS[statusClass(r.status)],
  query: queryString(r.query_params),
  timeText: formatGroupDate(r.created_at) + ', ' + formatGroupTime(r.created_at),
})))

async function openRequest(row: any) {
  selected.value = row
  drawerOpen.value = true
  bodiesLoading.value = false
  if (!row.has_request_body && !row.has_response_body) return
  // The list does not carry bodies; fetch this one row's.
  bodiesLoading.value = true
  try {
    const config = await authConfig()
    const res = await axios.get(`${baseUrl}/api/requests?id=${encodeURIComponent(row.id)}`, config)
    const full = Array.isArray(res.data.requests) ? res.data.requests[0] : null
    // Ignore the answer if another row was opened in the meantime.
    if (full && selected.value && selected.value.id === row.id) {
      selected.value = { ...selected.value, request_body: full.request_body, response_body: full.response_body }
    }
  } catch (err: any) {
    if (!handleAuthError(err)) {
      proxy.$notify({ type: 'danger', message: 'Could not load the request body', timeout: 3000 })
    }
  } finally {
    if (selected.value && selected.value.id === row.id) bodiesLoading.value = false
  }
}

// --- Data ----------------------------------------------------------------------
async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function handleAuthError(err: any): boolean {
  const status = err?.response?.status
  if (status === 401 || status === 403) {
    showDialog({ variant: 'danger', title: 'No access', message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', redirect: '/' })
    return true
  }
  return false
}

async function fetchRequests() {
  refreshing.value = true
  try {
    const config = await authConfig()
    const res = await axios.get(listQuery(perPage.value, pageOffset(currentPage.value, perPage.value)), config)
    requests.value = Array.isArray(res.data.requests) ? res.data.requests : []
    totalRows.value = Number(res.data.total_rows) || 0
    keys.value = Array.isArray(res.data.keys) ? res.data.keys : []
    syncPagingQuery(proxy.$router, proxy.$route, currentPage.value, perPage.value)
  } catch (err: any) {
    if (!handleAuthError(err)) {
      showDialog({ variant: 'danger', title: 'Could not load requests', message: 'Something went wrong. Please refresh and try again.' })
    }
  } finally {
    loading.value = false
    refreshing.value = false
  }
}

// --- CSV export ------------------------------------------------------------------
// Every request matching the current filters, newest first. Pages of 1000 follow a
// cursor (the last row of the previous page) rather than an offset, so a long export
// stays fast. The file is built in the browser, so it is capped.
const EXPORT_BATCH = 1000
const EXPORT_MAX_ROWS = 100000

async function downloadRequests() {
  loading.value = true
  try {
    const config = await authConfig()
    const all: any[] = []
    let cursor: any = null
    let total = 0
    for (;;) {
      const params = filterParams()
      params.set('count', String(EXPORT_BATCH))
      if (cursor) {
        params.set('before_created_at', cursor.created_at)
        params.set('before_id', String(cursor.id))
        params.set('no_count', '1')
      }
      const res = await axios.get(`${baseUrl}/api/requests?${params.toString()}`, config)
      const page: any[] = Array.isArray(res.data.requests) ? res.data.requests : []
      if (!cursor) total = Number(res.data.total_rows) || 0
      // Stop if the server did not move past the cursor, so a page can never repeat.
      if (cursor && page.length && Number(page[0].id) >= Number(cursor.id)) break
      all.push(...page)
      if (page.length < EXPORT_BATCH || all.length >= EXPORT_MAX_ROWS) break
      cursor = page[page.length - 1]
    }
    const keyIds = new Map(keys.value.map(k => [Number(k.id), k.api_id]))
    const when = (r: any) => formatGroupDate(r.created_at, { dayOfWeek: false }) + ' ' + formatGroupTime(r.created_at)
    downloadCsv('api-requests.csv', [
      { label: 'Time', value: (r: any) => when(r) },
      { label: 'Status', value: (r: any) => (r.status === null || r.status === undefined ? 'No response' : r.status) },
      { label: 'Method', value: (r: any) => r.method },
      { label: 'Endpoint', value: (r: any) => r.path },
      { label: 'Query parameters', value: (r: any) => queryString(r.query_params) },
      { label: 'Duration (ms)', value: (r: any) => (r.duration_ms === null || r.duration_ms === undefined ? '' : r.duration_ms) },
      { label: 'IP address', value: (r: any) => r.ip || '' },
      { label: 'User agent', value: (r: any) => r.user_agent || '' },
      { label: 'Request ID', value: (r: any) => r.request_id || '' },
      { label: 'API key ID', value: (r: any) => keyIds.get(Number(r.api_key_id)) || '' },
      { label: 'Error', value: (r: any) => r.error_message || '' },
    ], all)
    if (total > all.length) {
      showDialog({ variant: 'info', title: 'Export limit reached', message: `The file holds the ${all.length} most recent of ${total} requests. Narrow the date range to export the rest.` })
    }
  } catch (err: any) {
    if (!handleAuthError(err)) {
      proxy.$notify({ type: 'danger', message: 'Failed to export requests', timeout: 3000 })
    }
  } finally {
    loading.value = false
  }
}

// --- Dialog ---------------------------------------------------------------------
const dialog = reactive({
  show: false,
  variant: 'warning',
  title: '',
  message: '',
  showCancel: false,
  confirmText: 'Ok',
  confirmVariant: 'primary',
  redirect: '',
})

function showDialog(opts: Partial<typeof dialog>) {
  Object.assign(dialog, { showCancel: false, confirmText: 'Ok', confirmVariant: 'primary', redirect: '' }, opts, { show: true })
}

function onDialogConfirm() {
  dialog.show = false
  const redirect = dialog.redirect
  dialog.redirect = ''
  if (redirect) proxy.$router.push(redirect)
}

// --- Mount ---------------------------------------------------------------------
onMounted(() => {
  const paging = readPagingQuery(proxy.$route, 25)
  perPage.value = perPageOptions.includes(paging.perPage) ? paging.perPage : 25
  currentPage.value = paging.page
  // The filter bar emits one initial change after resolving saved views, which
  // triggers the first fetch (onFiltersChanged). Fall back if it never fires.
  setTimeout(() => { if (!didInitialFetch) { didInitialFetch = true; fetchRequests() } }, 800)
})
</script>

<script lang="ts">
export default { name: 'ApiRequests' }
</script>
